import { useSelector } from "react-redux";
import type { StateType } from "../backend/store";
import { PermissionRoute } from "./permissionRoute";
import { guardFor } from "./guardedPaths";
import { RedirectToLanding } from "./redirectToLanding";
import { ErrorState, LoadingSkeleton } from "../next/ui";
import { useNextT } from "../next/i18n";
import { useAccess } from "../next/access/api";
import { routesOpen } from "../next/savedRoutes/routesAccess";
import { SwitchRoute } from "../next/routes/SwitchRoute";
import { findEntry } from "../next/routes/switchboard";
import type { SwitchEntry } from "../next/routes/switchboard";

export { routesOpen };

type RoutesPath = "/saved-routes" | "/saved-routes/:id";

/**
 * Lives with today's guards (src/next may not import them). The guard of /saved-routes and /saved-routes/:id, aware of the switchboard (like MapRoute):
 *  - the new page is served: it follows the access answer (B1 of the routes walkthrough: Team members who plan routes hold no
 *    `read_routes`, so today's guard sent them to Contacts while the menu offered Routes);
 *  - today's page is served: exactly today's guard (`read_routes` with the routes scope).
 */
export const RoutesRoute = ({ path, board }: { path: RoutesPath; board?: SwitchEntry[] }) => {
  const mode = findEntry(path, board)?.entry.mode ?? "legacy";
  if (mode === "next") return <AccessGuard path={path} board={board} />;
  const guard = path === "/saved-routes" ? { permission: "read_routes", scopeType: "routes" as const } : guardFor("/saved-routes/:id");
  return (
    <PermissionRoute {...guard}>
      <SwitchRoute path={path} board={board} />
    </PermissionRoute>
  );
};

const AccessGuard = ({ path, board }: { path: RoutesPath; board?: SwitchEntry[] }) => {
  const { t } = useNextT();
  const user = useSelector((s: StateType) => s.authStore.currentUser);
  const { data, isLoading, error, refetch } = useAccess();
  if (error && !data) return <div className="cy-next"><ErrorState title={t("map.access.failed_title")} body={t("map.access.failed_body")} onRetry={() => { void refetch?.(); }} /></div>;
  if (isLoading || !data) return <div className="cy-next"><LoadingSkeleton rows={4} label={t("map.access.loading")} /></div>;
  if (!routesOpen(data.permissions)) return <RedirectToLanding user={user} />;
  return <SwitchRoute path={path} board={board} />;
};
